import type { TFunction } from 'i18next';
import type { PredefinedPartLabel } from '../types';
import { partLabelSchema } from './cadPartLabels.schema';
import type { TranslationFallbackOptions } from './translation';
import { translateWithFallback } from './translation';

export type PartLabelValidationError = 'invalid-format' | 'duplicate-label';

export type PartLabelValidationResult = (
  | { valid: true; partLabel: PredefinedPartLabel }
  | { valid: false; error: PartLabelValidationError }
);

const normalizePartLabelName = (label: string): string => (
  label.trim().toLowerCase()
);

export const validatePartLabel = (
  candidate: unknown,
  existingLabels: readonly PredefinedPartLabel[] = [],
): PartLabelValidationResult => {
  const parsed = partLabelSchema.safeParse(candidate);
  if (!parsed.success) {
    return { valid: false, error: 'invalid-format' };
  }

  const partLabel = parsed.data;
  if (
    existingLabels.some(
      (existing) =>
        normalizePartLabelName(existing.label) === normalizePartLabelName(partLabel.label),
    )
  ) {
    return { valid: false, error: 'duplicate-label' };
  }

  return { valid: true, partLabel };
};

export const parsePartLabelsJson = (
  raw: string,
  t?: TFunction,
): PredefinedPartLabel[] => {
  const trimmed = raw.trim();
  if (!trimmed) return [];

  const localize = (key: string, fallback: string, options: TranslationFallbackOptions = {}) => (
    t ? translateWithFallback(t, key, fallback, options) : fallback
  );

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    throw new Error(localize('drbfm.cadLink.parseError', '部位ラベルJSONを読み取れません'));
  }

  const parts = Array.isArray(parsed)
    ? parsed
    : (
      parsed
      && typeof parsed === 'object'
      && 'parts' in parsed
        ? (parsed as { parts?: unknown }).parts
        : undefined
    );

  if (!Array.isArray(parts)) {
    throw new Error(localize(
      'drbfm.cadLink.partsArrayRequired',
      'parts配列、または部位ラベル配列を指定してください',
    ));
  }

  const labels: PredefinedPartLabel[] = [];
  parts.forEach((part, index) => {
    const result = validatePartLabel(part, labels);
    if (!result.valid) {
      const position = index + 1;
      if (result.error === 'duplicate-label') {
        throw new Error(localize(
          'drbfm.cadLink.duplicatePartLabel',
          `部位ラベル${position}の部位名が重複しています`,
          { index: position },
        ));
      }
      throw new Error(localize(
        'drbfm.cadLink.invalidPartLabel',
        `部位ラベル${position}の形式が正しくありません`,
        { index: position },
      ));
    }

    labels.push(result.partLabel);
  });
  return labels;
};
